import React from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { generateDataTestId } from '../../../../utils';

interface CoordinatesFieldProps {
  latitude: string | number | null;
  longitude: string | number | null;
  locationType: string;
  noTaxRecordModeRef: React.MutableRefObject<boolean>;
  handleCoordinateChange: (
    field: 'latitude' | 'longitude',
    value: string,
  ) => void;
}

/**
 * CoordinatesField component - handles latitude/longitude inputs
 *
 * This component provides input fields for entering latitude and longitude coordinates
 * with proper validation and handling of disabled states.
 */
const CoordinatesField: React.FC<CoordinatesFieldProps> = ({
  latitude,
  longitude,
  locationType,
  noTaxRecordModeRef,
  handleCoordinateChange,
}) => {
  // A bit of a drift from standard disabled fields (all grey background color)
  // This was a design decision by UX -- let's handle this one-off: if we see this again, let's make this a theme extension
  const inputTheme = (theme: Theme) => ({
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
      '&.Mui-disabled': {
        backgroundColor: 'background.default',
        // Only change the text color, not the background
        '& input': {
          color: 'text.disabled',
        },
        '& fieldset': {
          borderColor: theme.palette.grey[300],
        },
      },
    },
  });

  const labelTheme = () => ({
    color: locationType !== 'coordinates' ? 'text.disabled' : null,
  });

  return (
    <Grid
      container
      spacing={2}
      mt={{
        md: 3,
      }}
    >
      <Grid
        size={{
          lg: 6,
          xl: 3,
          xs: 6,
        }}
      >
        <Typography fontWeight="bold" mb={1} sx={labelTheme}>
          Latitude
        </Typography>
        <TextField
          data-testid={generateDataTestId('Latitude', undefined, 'textField')}
          fullWidth
          placeholder="Enter Latitude"
          sx={inputTheme}
          variant="outlined"
          value={latitude || ''}
          disabled={
            locationType !== 'coordinates' || noTaxRecordModeRef.current
          }
          onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
          inputProps={{ inputMode: 'decimal' }}
        />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xl: 3,
          xs: 6,
        }}
      >
        <Typography fontWeight="bold" mb={1} sx={labelTheme}>
          Longitude
        </Typography>
        <TextField
          data-testid={generateDataTestId('Longitude', undefined, 'textField')}
          fullWidth
          placeholder="Enter Longitude"
          sx={inputTheme}
          variant="outlined"
          value={longitude || ''}
          disabled={
            locationType !== 'coordinates' || noTaxRecordModeRef.current
          }
          onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
          inputProps={{ inputMode: 'decimal' }}
        />
      </Grid>
    </Grid>
  );
};

export default CoordinatesField;
